import React from "react";
import { format, formatDistanceToNow, startOfDay } from "date-fns";
import { Pencil, FileExclamationPoint } from "lucide-react";

import { DatePicker } from "components/Graphs/DateRangePicker";
import { Button } from "components/Graphs/Button";
import { Input } from "components/Graphs/Input";
import CopyField from "components/UI-lib/CopyField";
import { StepHeading } from "../components";
import { maximumKeyExpirationDate } from "../utils";

export const APIKeyStep: React.FC<{
  activeOrgId: string | undefined;
  apiKeyName: string;
  canCreateNewKey: boolean;
  createKeyError: string;
  hasCompletedStepOne: boolean;
  hasValidAPIKey: boolean;
  isCreatingKey: boolean;
  isEditingAPIKeyName: boolean;
  isEditingKeyExpirationDate: boolean;
  isKeyExpirationDatePickerOpen: boolean;
  keyExpirationDate: Date;
  keyExpirationDateLabel: string;
  newAPIKey: string;
  onAPIKeyNameChange: (value: string) => void;
  onKeyExpirationDateChange: (date: Date | undefined) => void;
  onKeyExpirationDatePickerOpenChange: (isOpen: boolean) => void;
  onStartEditingAPIKeyName: () => void;
  onStartEditingKeyExpirationDate: () => void;
  orgDisplayName: string;
  createNewAPIKey: () => Promise<void>;
}> = ({ ...props }) => (
  <section
    className={`flex flex-col gap-3 rounded-lg border border-zinc-800 px-4 py-5 ${
      !props.hasCompletedStepOne ? "opacity-60" : ""
    }`}
  >
    <StepHeading number={2} isReady={props.hasValidAPIKey} highlightWhenReady>
      Create a Brev Connect registration API key
    </StepHeading>
    <div className="ml-9 flex min-w-0 flex-col gap-3">
      <p className="text-sm leading-5 text-secondary">
        This will create a new Personal API key with Read &amp; Write access
        used to register this device.
      </p>

      <div>
        {props.newAPIKey ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-white font-bold">
              <FileExclamationPoint className="inline h-4 w-4 mt-[-2px]" /> Copy
              and store this key now. It will never be shown again.
            </p>
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex min-w-0 items-center gap-1.5">
                <p className="text-sm font-medium text-white">API key</p>
                <p className="truncate text-sm text-secondary">
                  (expires on {format(props.keyExpirationDate, "MMM d, yyyy")})
                </p>
              </div>
              <CopyField
                value={props.newAPIKey}
                analytics={{
                  userid: "",
                  eventLabel: "copy Brev Connect API key",
                }}
                className="w-full font-mono"
              />
              <p className="text-sm text-secondary pt-1">
                Authenticate Brev CLI commands from this device using{" "}
                <code className="rounded bg-zinc-900 px-1.5 py-0.5 font-mono text-[12px] text-zinc-200">
                  --api-key &lt;key&gt;
                </code>
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-1">
                {props.isEditingAPIKeyName ? (
                  <>
                    <label
                      htmlFor="register-compute-key-name"
                      className="text-sm font-medium text-white"
                    >
                      API key name
                    </label>
                    <Input
                      id="register-compute-key-name"
                      type="text"
                      value={props.apiKeyName}
                      onChange={(event) =>
                        props.onAPIKeyNameChange(event.target.value)
                      }
                      disabled={props.isCreatingKey}
                      aria-invalid={!props.apiKeyName.trim() || undefined}
                      hasError={!props.apiKeyName.trim()}
                      className="min-w-0"
                    />
                    {!props.apiKeyName.trim() ? (
                      <p className="text-sm text-red-400" role="alert">
                        API key name is required.
                      </p>
                    ) : null}
                  </>
                ) : (
                  <>
                    <label className="text-sm font-medium text-white">
                      API key name
                    </label>
                    <div className="flex min-w-0 items-center gap-1">
                      <span className="truncate text-sm text-secondary">
                        {props.apiKeyName}
                      </span>
                      <button
                        type="button"
                        aria-label="Edit API key name"
                        className="flex h-[22px] w-[22px] shrink-0 cursor-pointer items-center justify-center rounded-sm border-0 bg-transparent p-0 text-zinc-500 transition-colors duration-150 hover:text-zinc-200 disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={props.onStartEditingAPIKeyName}
                        disabled={
                          props.isCreatingKey || !props.hasCompletedStepOne
                        }
                      >
                        <Pencil
                          aria-hidden="true"
                          size={13}
                          strokeWidth={1.8}
                        />
                      </button>
                    </div>
                  </>
                )}
              </div>
              <div className="flex min-w-0 flex-col gap-1">
                <label
                  id="register-compute-key-expiration-label"
                  className="text-sm font-medium text-white"
                >
                  Expiration date
                </label>
                {props.isEditingKeyExpirationDate ? (
                  <DatePicker
                    aria-labelledby="register-compute-key-expiration-label"
                    value={props.keyExpirationDate}
                    onChange={(date) => props.onKeyExpirationDateChange(date)}
                    open={props.isKeyExpirationDatePickerOpen}
                    onOpenChange={props.onKeyExpirationDatePickerOpenChange}
                    disabled={props.isCreatingKey}
                    fromDate={startOfDay(new Date())}
                    toDate={maximumKeyExpirationDate()}
                    required
                    placeholder="Select expiration date"
                    align="end"
                    className="bg-secondaryBg disabled:bg-secondaryBg disabled:dark:bg-secondaryBg"
                    applyOnSelect
                    renderValue={(date, formattedDate) => (
                      <>
                        {formattedDate} at 11:59PM (
                        {formatDistanceToNow(date, { addSuffix: true })})
                      </>
                    )}
                  />
                ) : (
                  <div className="flex min-w-0 items-center gap-1">
                    <span className="truncate text-sm text-secondary">
                      {props.keyExpirationDateLabel}
                    </span>
                    <button
                      type="button"
                      aria-label="Edit API key expiration date"
                      className="flex h-[22px] w-[22px] shrink-0 cursor-pointer items-center justify-center rounded-sm border-0 bg-transparent p-0 text-zinc-500 transition-colors duration-150 hover:text-zinc-200 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={props.onStartEditingKeyExpirationDate}
                      disabled={
                        props.isCreatingKey || !props.hasCompletedStepOne
                      }
                    >
                      <Pencil aria-hidden="true" size={13} strokeWidth={1.8} />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <Button
              variant={props.hasCompletedStepOne ? "primary" : "secondary"}
              onClick={props.createNewAPIKey}
              className="self-start"
              disabled={!props.canCreateNewKey}
              isLoading={props.isCreatingKey}
              loadingText="Creating key"
            >
              Create new key
            </Button>
          </div>
        )}
        {!props.activeOrgId ? (
          <p className="mt-2 text-sm text-red-400">
            Select an organization before creating a key.
          </p>
        ) : null}
        {props.createKeyError ? (
          <p className="mt-2 text-sm text-red-400" role="alert">
            {props.createKeyError}
          </p>
        ) : null}
      </div>
    </div>
  </section>
);
